<template>
  <div>
    <div class="form--group">
      <div class="left--col">
        <div class="d-flex align-center">
          <label for="search-place-field">
            <span>{{ $t('addListing.pin.title') }}</span>
            <!--            <span class="required">*</span>-->
          </label>
          <!--          <img src="@/assets/img/icons/info.svg" class="info--icon" id="mapInfo" />-->
        </div>
        <ListingScoreField field-code="VALID_GEO_POINT" />
      </div>
      <div class="right--col">
        <button @click="toggleUsePinLocation" class="btn btn-primary--light btn-block pin--btn">
          {{
            this.usePinLocation
              ? $t('addListing.removePinLocation')
              : $t('addListing.editPinLocation')
          }}
        </button>
        <search-places
          v-show="usePinLocation"
          @placeDetailFound="placeDetailFound"
          class="input--margin"
        />
      </div>
    </div>
    <div v-show="usePinLocation" class="pin-map-wrapper">
      <section-loading :show="loadingMap" />
      <div ref="mapContainer" class="map--container"></div>
      <p class="map-caption" role="note">
        <v-icon small aria-hidden="true">mdi-information-outline</v-icon>
        <span
          >Titik ini tidak akan dilihat oleh pencari properti, karena pencari properti hanya melihat
          radius lokasi.</span
        >
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import SectionLoading from '@/components/content-loading/section-loading';
import ListingScoreField from '@/components/listing-form/listing-score-field.vue';
import mapTileConfig from '@/config/map-tiles';

const SearchPlaces = () => import('@/components/listing-form/sect1/map/search-places.vue');
const DEFAULT_CENTER = [-6.1753924, 106.8249641];
const PIN_ICON_URL = '/img/map_pin.svg';

export default {
  name: 'map-section',
  components: { SearchPlaces, SectionLoading, ListingScoreField },
  data() {
    return {
      map: null,
      marker: null,
      detailZoom: 15,
      mapCreated: false,
      initializing: false,
      pendingDetailZoom: false,
      mapResizeObserver: null,
    };
  },
  async mounted() {
    if (this.usePinLocation) {
      await this.$nextTick();
      await this.initMap();
    }
  },
  beforeDestroy() {
    if (this.mapResizeObserver) {
      this.mapResizeObserver.disconnect();
      this.mapResizeObserver = null;
    }
    if (this.map) {
      this.map.remove();
    }
    this.map = null;
    this.marker = null;
    this.mapCreated = false;
  },
  computed: {
    ...mapState({
      form: (state) => state.listingForm.form,
      loadingMap: (state) => state.listingForm.sect1.location.map.loading,
    }),
    usePinLocation: {
      get() {
        return this.$store.state.listingForm.sect1.location.map.usePinLocation;
      },
      set(value) {
        this.$store.commit('listingForm/sect1/location/map/SET_USE_PIN_LOCATION', value);
      },
    },
    latitude: {
      get() {
        return this.$store.state.listingForm.sect1.location.map.lat;
      },
      set(value) {
        this.$store.commit('listingForm/sect1/location/map/SET_LAT', value);
      },
    },
    longitude: {
      get() {
        return this.$store.state.listingForm.sect1.location.map.lng;
      },
      set(value) {
        this.$store.commit('listingForm/sect1/location/map/SET_LNG', value);
      },
    },
  },
  watch: {
    latitude() {
      this.changeLocationByLatLong(this.latitude, this.longitude);
    },
    longitude() {
      this.changeLocationByLatLong(this.latitude, this.longitude);
    },
    usePinLocation(enabled) {
      if (!enabled) return;

      this.$nextTick(() => {
        if (this.map) {
          this.invalidateMapSize();
          this.changeLocationByLatLong(this.latitude, this.longitude);
        } else {
          this.initMap();
        }
      });
    },
  },
  methods: {
    observeMapSize() {
      const container = this.$refs.mapContainer;
      if (!container || typeof ResizeObserver === 'undefined') return;

      if (this.mapResizeObserver) this.mapResizeObserver.disconnect();
      this.mapResizeObserver = new ResizeObserver(([entry]) => {
        if (!this.map || !this.usePinLocation || !entry) return;
        if (entry.contentRect.width <= 0 || entry.contentRect.height <= 0) return;

        this.map.invalidateSize({ pan: false, debounceMoveend: true });
      });
      this.mapResizeObserver.observe(container);
    },
    invalidateMapSize() {
      if (!this.map) return;

      window.requestAnimationFrame(() => {
        if (!this.map || !this.usePinLocation) return;
        this.map.invalidateSize({ pan: false, debounceMoveend: true });
      });
    },
    toggleUsePinLocation() {
      if (this.usePinLocation) {
        this.usePinLocation = false;
        this.latitude = null;
        this.longitude = null;
      } else {
        this.latitude = DEFAULT_CENTER[0];
        this.longitude = DEFAULT_CENTER[1];
        this.usePinLocation = true;
      }
    },
    getCoordinates() {
      if (
        this.latitude === null ||
        this.longitude === null ||
        this.latitude === '' ||
        this.longitude === ''
      ) {
        return null;
      }

      const latitude = Number(this.latitude);
      const longitude = Number(this.longitude);
      return Number.isFinite(latitude) && Number.isFinite(longitude) ? [latitude, longitude] : null;
    },
    placeDetailFound() {
      if (!this.mapCreated) {
        this.pendingDetailZoom = true;
        return;
      }

      const coordinates = this.getCoordinates();
      if (coordinates) this.map.setView(coordinates, this.detailZoom);
    },
    changeLocationByLatLong(latitude, longitude) {
      const coordinates =
        latitude === null || longitude === null ? null : [Number(latitude), Number(longitude)];
      if (
        !this.mapCreated ||
        !this.map ||
        !this.marker ||
        !coordinates ||
        !coordinates.every(Number.isFinite)
      ) {
        return;
      }

      this.marker.setLatLng(coordinates);
      this.map.panTo(coordinates);
    },
    async initMap() {
      if (this.map || this.initializing || !this.usePinLocation) return;

      this.initializing = true;
      try {
        const L = await import('leaflet');
        if (this._isDestroyed || !this.usePinLocation || !this.$refs.mapContainer) return;

        if (this.latitude === null || this.longitude === null) {
          this.latitude = DEFAULT_CENTER[0];
          this.longitude = DEFAULT_CENTER[1];
        }
        const coordinates = this.getCoordinates() || DEFAULT_CENTER;
        const initialZoom = this.pendingDetailZoom || this.form !== 'add' ? this.detailZoom : 5;

        this.map = L.map(this.$refs.mapContainer, { maxZoom: mapTileConfig.maxZoom }).setView(
          coordinates,
          initialZoom,
        );
        this.map.attributionControl.setPrefix(false);
        this.observeMapSize();
        L.tileLayer(mapTileConfig.urlTemplate, {
          attribution: mapTileConfig.attribution,
          maxZoom: mapTileConfig.maxZoom,
        }).addTo(this.map);
        this.marker = L.marker(coordinates, {
          draggable: true,
          icon: L.icon({
            iconUrl: PIN_ICON_URL,
            iconSize: [50, 50],
            iconAnchor: [25, 50],
          }),
        }).addTo(this.map);
        this.marker.on('dragend', (event) => {
          const position = event.target.getLatLng();
          this.latitude = position.lat;
          this.longitude = position.lng;
          if (this.map.getZoom() > this.detailZoom) this.map.setZoom(this.detailZoom);
        });

        this.mapCreated = true;
        this.pendingDetailZoom = false;
        await this.$nextTick();
        this.invalidateMapSize();
      } finally {
        this.initializing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/scss/utils/variables';
.pin-map-wrapper {
  position: relative;
  z-index: 0;
}
.map--container {
  height: 350px;
  border-radius: 12px;
  margin-bottom: 8px;
  overflow: hidden;
}
.pin--btn {
  margin-bottom: 12px;
}
.map-caption {
  display: flex;
  min-height: 44px;
  align-items: flex-start;
  gap: 8px;
  margin: 10px 0 20px;
  padding: 11px 14px;
  border: 1px solid rgba($color-beliruma-1, 0.28);
  border-radius: 10px;
  background: rgba($color-beliruma-1, 0.04);
  color: $color-text-gray-2;
  font-size: 12px;
  line-height: 1.5;

  .v-icon {
    flex: 0 0 auto;
    margin-top: 1px;
    color: $color-beliruma-1;
    font-size: 18px;
  }
}
</style>
